import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography, Paper, Link } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Cancel";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import "../components/chatwidget/chatwidget.css";
import chatwidgetService from "../services/chatwidget.service";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import ChatForm from "../components/chatwidget/ChatForm";
import ChatLauncher from "../components/chatwidget/ChatLauncher";
import ChatFooter from "../components/chatwidget/ChatFooter";
import ChatHeader from "../components/chatwidget/ChatHeader";
import FeedbackButtons from "../components/chatwidget/FeedbackButtons";
import PromptList from "../components/chatwidget/PromptList";
import PdfDrawer from "../components/chatwidget/PdfDrawer";
import InfoIcon from "@mui/icons-material/Info";
import { useSnackbar } from "notistack";
import { Helmet } from "react-helmet";
import { CalendarMonth, Email } from "@mui/icons-material";
import GoogleCalendarDialogPublic from "./integration/GoogleCalendarDialogPublic";
import EmailLeadForm from "../components/chatwidget/EmailLeadForm";

const Chatwidget = ({ agentData, kbId }) => {
  console.log("Chatwidget kbId:", kbId);

  // All hooks at top level
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [isAllowed, setIsAllowed] = useState(false);
  const [layout, setLayout] = useState("center");
  const [selectedImage, setSelectedImage] = useState(null);
  const messagesEndRef = useRef(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showForm, setShowForm] = useState(true);
  const [webDetail, setWebDetail] = useState(null);
  const [userSession, setUserSession] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [connections, setConnections] = useState({ google: false });
  const [googleCalendarDialogOpen, setGoogleCalendarDialogOpen] =
    useState(false);
  const [emailLeadsOpen, setEmailLeadsOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();
  const [drawerState, setDrawerState] = useState({
    visible: false,
    documentId: null,
    selectedPages: [],
  });

  const streamingAnswerRef = useRef("");
  const hasSavedRef = useRef(false);
  const streamDoneRef = useRef(false);

  // Extract userId safely
  const calendarUserId = webDetail?.websiteDetails?.user?.id;

  // Hooks must run unconditionally
  const checkConnections = useCallback(async () => {
    if (!calendarUserId) return;

    try {
      const googleRes = await chatwidgetService.getConnection(calendarUserId);
      setConnections({
        google: googleRes?.success && !!googleRes?.data?.accessToken,
      });
    } catch (err) {
      console.error("❌ Google connection error:", err);
      setConnections({ google: false });
    }
  }, [calendarUserId]);

  useEffect(() => {
    if (calendarUserId) checkConnections();
  }, [calendarUserId, checkConnections]);

  useEffect(() => {
    const checkWebsite = async () => {
      // let siteurl =
      //   agentData?.websiteUrl ||
      //   `${window.location.protocol}//${window.location.host}`;
      // console.log("siteurl---->", siteurl);

      try {
        const response = await chatwidgetService.checkWebsite({
          // siteurl,
          kbId,
        });
        console.log("response------->", response);

        if (response.allowed) {
          setIsAllowed(true);
          setLayout(response?.websiteDetails?.layout);
          setWebDetail(response);
        } else setIsAllowed(false);
      } catch (err) {
        setIsAllowed(false);
        console.log("checkWebsite error.message--->", err.message);
      }
    };
    checkWebsite();
  }, [enqueueSnackbar, kbId]);

  useEffect(() => {
    if (transcript) setUserInput(transcript);
  }, [transcript]);

  useEffect(() => {
    const storedUserSessionStr = localStorage.getItem("userSession");
    if (storedUserSessionStr && storedUserSessionStr !== "null") {
      try {
        const storedUserSession = JSON.parse(storedUserSessionStr);
        if (
          storedUserSession &&
          (storedUserSession.user || storedUserSession.id)
        ) {
          setShowForm(false);
          setUserSession(storedUserSession);
          storedUserSession?.id && fetchChatMessages(storedUserSession.id);
        } else {
          setShowForm(true);
        }
      } catch (e) {
        console.error("Error parsing userSession:", e);
        setShowForm(true);
      }
    } else {
      setShowForm(true);
    }
  }, []);

  const fetchChatMessages = async (sessionId) => {
    try {
      const chatMessages = await chatwidgetService.getChatMessages(sessionId);

      // Check if data exists and is non-empty
      if (!chatMessages?.details || chatMessages.details.length === 0) {
        console.warn("No messages found for this session. Clearing session.");
        localStorage.removeItem("userSession");
        setUserSession(null);
        setShowForm(true);
        return;
      }

      const formattedMessages = chatMessages.details.flatMap((msg) => {
        const messagesArray = [];

        if (msg.question) {
          messagesArray.push({
            id: msg.id,
            sender: "question",
            message: msg.question,
            image: msg.image || null,
          });
        }

        if (msg.answer) {
          messagesArray.push({
            id: msg.id,
            sender: "answer",
            message: msg.answer || "No answer for this question.", // keep it string
            image: null,
            feedback: msg.feedback || null,
            completeResponse: {
              message: msg.completeResponse.answer,
              ans: msg.completeResponse.ans || msg.completeResponse.references,
              session_id: msg.sessionId,
            },
          });
        }

        return messagesArray;
      });

      setMessages(formattedMessages);
    } catch (error) {
      console.error("Error fetching chat messages:", error.message);
      localStorage.removeItem("userSession");
      setUserSession(null);
      setShowForm(true);
    }
  };

  useEffect(() => {
    if (userSession)
      localStorage.setItem("userSession", JSON.stringify(userSession));
  }, [userSession]);

  useEffect(() => {
    if (messagesEndRef.current)
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ✅ Conditional UI only after all hooks
  if (!browserSupportsSpeechRecognition)
    return <p>Browser does not support speech recognition.</p>;
  if (!isAllowed) return null;

  //   const [open, setOpen] = useState(false);
  //   const [messages, setMessages] = useState([]);
  //   const [userInput, setUserInput] = useState("");
  //   const [isAllowed, setIsAllowed] = useState(false);
  //   const [layout, setLayout] = useState("center");
  //   const [selectedImage, setSelectedImage] = useState(null);
  //   const messagesEndRef = useRef(null);
  //   const [imagePreview, setImagePreview] = useState(null);
  //   const [showForm, setShowForm] = useState(true);
  //   const [webDetail, setWebDetail] = useState(null);
  //   const [userSession, setUserSession] = useState(null);
  //   const [userInfo, setUserInfo] = useState(null);
  //   const [clearDialogOpen, setClearDialogOpen] = useState(false);
  //   const [googleEvents, setGoogleEvents] = useState([]);
  //   const [connections, setConnections] = useState({
  //     google: false,
  //   });

  //   // Google Calendar Dialog State
  //   const [googleCalendarDialogOpen, setGoogleCalendarDialogOpen] =
  //     useState(false);

  const openGoogleCalendar = () => {
    setGoogleCalendarDialogOpen(true);
  };

  //   // Email leads Dialog state
  //   const [emailLeadsOpen, setEmailLeadsOpen] = useState(false);

  //   const { enqueueSnackbar } = useSnackbar();

  //   const {
  //     transcript,
  //     listening,
  //     resetTranscript,
  //     browserSupportsSpeechRecognition,
  //   } = useSpeechRecognition();

  //   const [drawerState, setDrawerState] = useState({
  //     visible: false,
  //     documentId: null,
  //     selectedPages: [],
  //   });

  //   useEffect(() => {
  //     const checkWebsite = async () => {
  //       let siteurl = null;
  //       if (agentData && kbId) {
  //         console.log("agent data 66---->", agentData, kbId);
  //         siteurl = agentData?.websiteUrl;
  //       } else {
  //         const baseUrl = `${window.location.protocol}//${window.location.host}`;
  //         siteurl = baseUrl;
  //       }
  //       console.log("Site Url---->", siteurl);

  //       try {
  //         const response = await chatwidgetService.checkWebsite({
  //           siteurl,
  //           kbId,
  //         });
  //         console.log("checkWebsite response--->", response);

  //         if (response.allowed) {
  //           setIsAllowed(true);
  //           setLayout(response?.websiteDetails?.layout);
  //           setWebDetail(response);
  //         } else {
  //           setIsAllowed(false);
  //         }
  //       } catch (error) {
  //         setIsAllowed(false);
  //         console.log("checkWebsite error.message--->", error.message);
  //       }
  //     };

  //     checkWebsite();
  //   }, [enqueueSnackbar, agentData, kbId]);

  //   // Update input when transcript changes
  //   useEffect(() => {
  //     if (transcript) setUserInput(transcript);
  //   }, [transcript]);

  //   // Load session from localStorage on mount
  //   useEffect(() => {
  //     const storedUserSession = JSON.parse(localStorage.getItem("userSession"));
  //     console.log("storedUserSession--->", storedUserSession);

  //     if (storedUserSession != null) {
  //       setShowForm(false);
  //       setUserSession(storedUserSession);
  //       storedUserSession?.id && fetchChatMessages(storedUserSession.id);
  //     } else {
  //       storedUserSession?.user && setShowForm(false);
  //     }
  //   }, []);

  //   // Persist session when updated
  //   useEffect(() => {
  //     if (userSession) {
  //       localStorage.setItem("userSession", JSON.stringify(userSession));
  //     }
  //   }, [userSession]);

  //   // Scroll chat to bottom on new message
  //   useEffect(() => {
  //     if (messagesEndRef.current) {
  //       messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  //     }
  //   }, [messages]);

  const handleOpenClearDialog = () => {
    // console.log("Clear Chat clicked");
    setClearDialogOpen(true);
  };

  const handleCloseClearDialog = () => setClearDialogOpen(false);

  const handleConfirmClearChat = () => {
    setMessages([]);
    setUserInput("");
    setSelectedImage(null);
    setImagePreview(null);

    const storedUserSession = JSON.parse(localStorage.getItem("userSession"));

    if (storedUserSession && storedUserSession.id) {
      delete storedUserSession.id;
    }

    console.log("storedUserSession----->", storedUserSession);

    localStorage.setItem("userSession", JSON.stringify(storedUserSession));
    setUserSession(storedUserSession);

    // localStorage.removeItem("userSession");
    setClearDialogOpen(false);
  };

  const toggleChat = () => setOpen((prev) => !prev);

  const handleChatFormSubmit = async (formData) => {
    try {
      console.log("handleChatFormSubmit webDetail---->", webDetail, formData);

      const { name, email } = formData;
      const websiteid = webDetail?.websiteDetails?.id;
      const userResponse = await chatwidgetService.createChatUser({
        name,
        email,
        websiteid,
      });
      console.log("userResponse---->", userResponse);

      // Save customer email for site owner to follow up
      try {
        await chatwidgetService.createEmailLead({
          userId: webDetail?.websiteDetails?.user?.id,
          agentId: websiteid,
          leadEmail: email,
          leadName: name,
          question: "Customer started a chat session",
          context: "Customer provided contact information to begin chatting",
          reason: "other",
          status: "new",
          user: webDetail?.websiteDetails?.user?.id,
        });
        console.log("Customer email saved for follow-up");
      } catch (leadError) {
        console.error(
          "Failed to save customer email (non-critical):",
          leadError,
        );
        // Don't block chat if email save fails
      }

      setUserInfo(userResponse);
      console.log("userInfo--->", userInfo);

      localStorage.setItem(
        "userSession",
        JSON.stringify({ user: userResponse }),
      );
      console.log(
        "userResponse--255-->",
        JSON.parse(localStorage.getItem("userSession")),
      );
      setUserSession(JSON.parse(localStorage.getItem("userSession")));
      setShowForm(false);
    } catch (err) {
      console.error("Chat form submission error:", err.message);
    }
  };

  const handleSendMessage = async (messageOverride) => {
    let messageStr = "";

    // ✅ Case 1: Prompt click / manual string
    if (typeof messageOverride === "string") {
      messageStr = messageOverride;
    }
    // ✅ Case 2: Mobile / button event
    else if (
      messageOverride &&
      typeof messageOverride === "object" &&
      messageOverride.preventDefault
    ) {
      messageOverride.preventDefault();
      messageStr = userInput;
    }
    // ✅ Case 3: Normal send button click
    else {
      messageStr = userInput;
    }

    if (!messageStr.trim() && !selectedImage) return;

    setUserInput("");
    hasSavedRef.current = false;
    streamingAnswerRef.current = "";
    streamDoneRef.current = false;

    const userMessage = {
      sender: "question",
      message: messageStr,
      image: selectedImage ? URL.createObjectURL(selectedImage) : null,
    };

    setMessages((prev) => [
      ...prev,
      userMessage,
      { sender: "answer", message: "loading" },
    ]);

    try {
      let currentSessionId = userSession?.id || "static-initial-id";
      const currentUserId = userInfo?.id || userSession?.user?.id;

      if (!currentUserId) {
        throw new Error("User not found. Please fill the form first.");
      }

      /* ================= IMAGE FLOW ================= */
      if (selectedImage) {
        const formData = new FormData();
        formData.append("image", selectedImage);

        const response = await chatwidgetService.uploadImage(formData);
        const imageReply = response?.message || "Image uploaded successfully!";

        setMessages((prev) => [
          ...prev.slice(0, -1),
          { sender: "answer", message: imageReply },
        ]);
        return;
      }

      /* ================= TEXT FLOW ================= */
      const datasetName = `${webDetail?.websiteDetails?.userId}/${webDetail?.websiteDetails?.uniqueKbSlug}`;

      const payloadSet = {
        dataset_name: datasetName,
        question: messageStr,
        session_id: currentSessionId,
      };

      const handleBotResponse = async (botResponse) => {
        const content =
          typeof botResponse === "string"
            ? botResponse
            : botResponse.output || botResponse.message || "";

        /* 🟡 STREAMING */
        if (!botResponse?.is_completed) {
          streamingAnswerRef.current = content;

          setMessages((prev) => {
            const updated = [...prev];
            updated[updated.length - 1] = {
              sender: "answer",
              message: content,
            };
            return updated;
          });
          return;
        }

        /* 🟢 FINAL (ONCE ONLY) */
        if (streamDoneRef.current) return;
        streamDoneRef.current = true;

        const finalAnswer = content || streamingAnswerRef.current;
        streamingAnswerRef.current = "";

        if (
          finalAnswer.includes(
            "The answer you are looking for is not found in the knowledge base",
          )
        ) {
          setMessages((prev) => [
            ...prev.slice(0, -1),
            {
              sender: "answer",
              message:
                "The answer you are looking for is not found in the knowledge base!",
              options: true,
            },
          ]);
          return;
        }

        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            sender: "answer",
            message: finalAnswer,
            completeResponse: {
              message: finalAnswer,
              ans: botResponse.references || botResponse.ans,
              session_id: botResponse.session_id,
            },
          };
          return updated;
        });

        /* CREATE SESSION */
        if (
          currentSessionId === "static-initial-id" &&
          botResponse.session_id
        ) {
          const sessionData = await chatwidgetService.createChatSession(
            currentUserId,
            botResponse.session_id,
            webDetail?.websiteDetails?.id,
          );
          setUserSession(sessionData);
          currentSessionId = botResponse.session_id;
          localStorage.setItem("userSession", JSON.stringify(sessionData));
        }

        /* SAVE TO DB (ONCE) */
        await chatwidgetService.syncToDBChatMessage(
          currentUserId,
          currentSessionId,
          messageStr,
          finalAnswer,
          {
            message: finalAnswer,
            ans: botResponse.references || botResponse.ans,
            session_id: botResponse.session_id,
          },
          webDetail?.websiteDetails?.id,
        );

        fetchChatMessages(currentSessionId);
      };

      await chatwidgetService.handleSendMessage(payloadSet, handleBotResponse);
    } catch (error) {
      console.error("Chat error:", error);

      setMessages((prev) => [
        ...prev.slice(0, -1),
        {
          sender: "answer",
          message:
            "Thanks for your question! We couldn't find the answer right now, but our team will review it and follow up soon.\n\nYou can schedule a meeting or leave your email so we can assist you better.",
          options: true,
        },
      ]);
    } finally {
      setSelectedImage(null);
      setOpen(true);
      resetTranscript();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleMicClick = () => {
    if (listening) {
      SpeechRecognition.stopListening();
    } else {
      SpeechRecognition.startListening({ continuous: true });
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setImagePreview(imageUrl);
      setSelectedImage(file);
    }
  };

  const handleRemoveImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setSelectedImage(null);
    setImagePreview(null);

    document.querySelector("input[type=file]").value = "";
  };

  if (!browserSupportsSpeechRecognition) {
    return <p>Browser does not support speech recognition.</p>;
  }

  const LoadingIndicator = () => (
    <div className="loading-indicator-container">
      <div className="loading-animation">
        <div className="dot-container">
          <div className="dot"></div>
          <div className="dot"></div>
          <div className="dot"></div>
        </div>
        {/* <span>I'm putting together a great answer for you... please wait.</span> */}
      </div>
    </div>
  );

  const handleOpenReference = (documentId, selectedPages) => {
    console.log("Opening reference:", {
      documentId,
      selectedPages,
      drawerState: drawerState,
    });
    setDrawerState({
      visible: true,
      documentId,
      selectedPages,
    });
  };

  const handleCloseDrawer = () => {
    console.log("Closing drawer");
    setDrawerState({
      visible: false,
      documentId: null,
      selectedPages: [],
    });
  };

  const renderMessageWithReferences = (
    text,
    references = [],
    handleOpenReference,
  ) => {
    if (!text) return null;

    const replaceReferences = (children) =>
      React.Children.map(children, (child, childIndex) => {
        if (typeof child === "string") {
          // Support both ##0$$ and [ID:0]
          const parts = child.split(/(##\d+\$\$|\[ID:\d+\])/g);

          return parts.map((part, index) => {
            const match = part.match(/##(\d+)\$\$|\[ID:(\d+)\]/);
            if (!match) return part;

            const refIndex = Number(match[1] ?? match[2]);
            const ref = references?.[refIndex];
            if (!ref) return part;

            const uniquePages = Array.isArray(ref.positions)
              ? [
                  ...new Set(
                    ref.positions
                      .filter((p) => Array.isArray(p) && p[0])
                      .map((p) => p[0]),
                  ),
                ]
              : [];

            return (
              <IconButton
                key={`ref-${childIndex}-${index}-${refIndex}`}
                onClick={() =>
                  handleOpenReference?.(ref.document_id, uniquePages)
                }
                size="small"
                sx={{
                  padding: "2px",
                  margin: "0 2px",
                  verticalAlign: "middle",
                }}
              >
                <InfoIcon sx={{ fontSize: 16, color: "#007BFF" }} />
              </IconButton>
            );
          });
        }

        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            key: child.key ?? childIndex,
            children: replaceReferences(child.props.children),
          });
        }

        return child;
      });

    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          p: ({ children }) => (
            <div style={{ marginBottom: 16, lineHeight: 1.6 }}>
              {replaceReferences(children)}
            </div>
          ),
          li: ({ children }) => (
            <li style={{ marginBottom: 8, lineHeight: 1.6 }}>
              {replaceReferences(children)}
            </li>
          ),
          ul: (props) => (
            <ul style={{ paddingLeft: 24, margin: "12px 0" }} {...props} />
          ),
          ol: (props) => (
            <ol style={{ paddingLeft: 24, margin: "12px 0" }} {...props} />
          ),
          strong: (props) => (
            <strong style={{ fontWeight: 600, color: "#000" }} {...props} />
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    );
  };

  const validPrompts = (webDetail?.websiteDetails?.defaultPrompts || []).filter(
    (p) => p && p.trim() !== "",
  );

  return (
    <>
      <Helmet>
        <title>Chat Interface - Your Website</title>
        <meta
          name="description"
          content="Chat with our assistant to get help and information."
        />
        <meta
          name="keywords"
          content="chat assistant, live chat, helpdesk chat, customer support, AI assistant, sitepilot chat, online chat support"
        />
      </Helmet>

      <Box
        className={
          open ? (layout === "center" ? "centered-chat" : "floating-chat") : ""
        }
      >
        {console.log("agent data---->", agentData)}
        {open ? (
          <Box
            className={
              layout === "center"
                ? "chat-box-centered common-design"
                : "chat-box common-design"
            }
          >
            <ChatHeader
              webDetail={webDetail}
              toggleChat={toggleChat}
              userInfo={userInfo}
              onClearChat={handleOpenClearDialog}
              messages={messages}
              googleEvents={googleEvents}
              setGoogleEvents={setGoogleEvents}
            />

            <Box className="chat-content">
              {showForm ? (
                <ChatForm
                  onSubmit={handleChatFormSubmit}
                  webDetail={webDetail}
                />
              ) : (
                <>
                  <div className="chat-content-main">
                    {messages.length === 0 && (
                      <>
                        <div className="prompt-header">
                          <div className="prompt-title">Hi there! </div>
                          <div className="prompt-subtitle">
                            What can I help you with today?
                          </div>
                        </div>

                        <div className="prompt-header">
                          <PromptList
                            prompts={validPrompts.length ? validPrompts : []}
                            onSelectPrompt={(selectedPrompt) => {
                              setUserInput(selectedPrompt);
                              handleSendMessage(selectedPrompt);
                            }}
                          />
                        </div>
                      </>
                    )}

                    {messages.map((msg, index) => {
                      // ✅ HARD SAFETY: message must always be a string
                      const safeMessage =
                        typeof msg.message === "string" ? msg.message : "";

                      return (
                        <Paper
                          key={index}
                          className={`chat-message ${msg.sender}`}
                        >
                          <Typography variant="body1" component="div">
                            {msg.sender === "answer" &&
                            safeMessage === "loading" ? (
                              <LoadingIndicator />
                            ) : (
                              <div>
                                {/* 🔥 CASE 1: FALLBACK MESSAGE WITH TWO OPTIONS */}
                                {msg.options ? (
                                  <div style={{ padding: "10px 0" }}>
                                    <div style={{ marginBottom: "8px" }}>
                                      {renderMessageWithReferences(safeMessage)}
                                    </div>

                                    {/* TWO OPTIONS */}
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: 1,
                                        mt: 1,
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      {connections.google && (
                                        <Button
                                          variant="contained"
                                          size="small"
                                          onClick={openGoogleCalendar}
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            textTransform: "none",
                                          }}
                                        >
                                          <CalendarMonth />
                                          Schedule a Meeting
                                        </Button>
                                      )}

                                      <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => setEmailLeadsOpen(true)}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                          textTransform: "none",
                                        }}
                                      >
                                        <Email />
                                        Email Follow Up
                                      </Button>
                                    </Box>
                                  </div>
                                ) : (
                                  <>
                                    {/* 🔥 CASE 2: NORMAL BOT ANSWER */}
                                    {msg.sender === "answer" ? (
                                      renderMessageWithReferences(
                                        safeMessage,
                                        Array.isArray(msg.completeResponse?.ans)
                                          ? msg.completeResponse.ans
                                          : [],
                                        handleOpenReference,
                                      )
                                    ) : (
                                      <div>
                                        {renderMessageWithReferences(
                                          safeMessage,
                                        )}
                                      </div>
                                    )}

                                    {/* 🔥 Feedback */}
                                    {msg.sender === "answer" &&
                                      !msg.options && (
                                        <FeedbackButtons
                                          chatDetailId={msg.id}
                                          feedbackRating={msg.feedback}
                                        />
                                      )}
                                  </>
                                )}

                                {/* IMAGE VIEWER */}
                                {msg.image && (
                                  <Box className="chat-image-container">
                                    <img
                                      src={msg.image}
                                      alt="Uploaded"
                                      className="chat-image"
                                    />
                                  </Box>
                                )}
                              </div>
                            )}
                          </Typography>
                        </Paper>
                      );
                    })}

                    <div ref={messagesEndRef} />
                  </div>

                  <Box
                    className={`show-images ${selectedImage ? "visible" : ""}`}
                  >
                    {selectedImage && (
                      <div className="image-preview-container">
                        <img
                          src={URL.createObjectURL(selectedImage)}
                          alt="Preview"
                          className="image-preview"
                        />
                        <IconButton
                          onClick={handleRemoveImage}
                          className="remove-image-btn"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </div>
                    )}
                  </Box>

                  {/* {webDetail?.websiteDetails?.status === "completed" && ( */}
                  <ChatFooter
                    selectedImage={selectedImage}
                    userInput={userInput}
                    listening={listening}
                    setUserInput={setUserInput}
                    handleRemoveImage={handleRemoveImage}
                    handleImageChange={handleImageChange}
                    handleMicClick={handleMicClick}
                    handleSendMessage={handleSendMessage}
                    handleKeyDown={handleKeyDown}
                  />
                  {/* )} */}
                </>
              )}
              <Box
                sx={{
                  textAlign: "center",
                  mt: 0.5,
                  fontSize: "12px",
                  color: "text.secondary",
                }}
              >
                Powered by ❤️
                <Link
                  href="https://www.sitepilot.online/"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  color="inherit"
                  fontWeight={500}
                >
                  sitepilot.online
                </Link>
              </Box>
            </Box>
          </Box>
        ) : (
          <ChatLauncher onClick={toggleChat} />
        )}
        <Dialog
          open={clearDialogOpen}
          onClose={handleCloseClearDialog}
          aria-labelledby="clear-chat-dialog-title"
          aria-describedby="clear-chat-dialog-description"
        >
          <DialogTitle id="clear-chat-dialog-title">Clear Chat</DialogTitle>
          <DialogContent>
            <DialogContentText id="clear-chat-dialog-description">
              Are you sure you want to clear the chat? This will remove all
              messages and return to the prompt list.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseClearDialog}>Cancel</Button>
            <Button
              onClick={handleConfirmClearChat}
              color="error"
              variant="contained"
            >
              Clear Chat
            </Button>
          </DialogActions>
        </Dialog>
        <PdfDrawer
          visible={drawerState.visible}
          documentId={drawerState.documentId}
          hideModal={handleCloseDrawer}
          selectedPages={drawerState.selectedPages}
        />
        {googleCalendarDialogOpen && (
          <GoogleCalendarDialogPublic
            open={googleCalendarDialogOpen}
            onClose={() => setGoogleCalendarDialogOpen(false)}
            webDetail={webDetail}
          />
        )}
        {emailLeadsOpen && (
          <Dialog
            open={emailLeadsOpen}
            onClose={() => setEmailLeadsOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <IconButton
              aria-label="close"
              onClick={() => setEmailLeadsOpen(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>

            <DialogTitle>Get in Touch</DialogTitle>
            <DialogContent>
              <EmailLeadForm
                userId={userSession?.user?.id}
                agentId={webDetail?.websiteDetails?.id}
                defaultEmail={userSession?.user?.email || ""}
                defaultName={userSession?.user?.name || ""}
                userSession={userSession}
                onSuccess={() => setEmailLeadsOpen(false)}
              />
            </DialogContent>
          </Dialog>
        )}
      </Box>
    </>
  );
};

export default Chatwidget;
